import React, { useState } from 'react';
import { Check, Star, Award, FileText, ShieldCheck, Send, ClipboardList, Scale, Briefcase } from 'lucide-react';
import { selfFilingProcessCards, attorneyLedProcessCards, selfFilingPricingPlans, attorneyLedPricingPlans } from '../mockData';
import UniversalPopupForm from './UniversalPopupForm';
import { usePricing } from '../context/PricingContext';

const iconMap = {
  FileText,
  ShieldCheck,
  Send,
  ClipboardList,
  Scale,
  Briefcase
};

const TAB_1 = 1;
const TAB_2 = 2;

const TAB_CONFIG = {
  [TAB_1]: {
    label: 'Self Filing with Assistance',
    processCards: selfFilingProcessCards,
    pricingPlans: selfFilingPricingPlans,
    headerTitle: 'Self-Filing with Expert Assistance',
    headerSubtitle: 'Complete Support Plan for Easy Filing'
  },
  [TAB_2]: {
    label: 'IP Attorney-Led Filing',
    processCards: attorneyLedProcessCards,
    pricingPlans: attorneyLedPricingPlans,
    headerTitle: 'Attorney-Led Filing',
    headerSubtitle: 'Complete End-to-End Service Plan'
  }
};

const ProcessCard = ({ card }) => {
  const Icon = iconMap[card.icon] || FileText;
  return (
    <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-6 shadow-lg border border-brand-100 transform transition-all duration-300 hover:scale-105 hover:shadow-xl">
      <div className="bg-brand-100 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
        <Icon className="w-7 h-7 text-brand-600" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{card.title}</h3>
      <p className="text-gray-600 leading-relaxed">{card.description}</p>
    </div>
  );
};

const PricingCard = ({ plan, onGetStarted }) => {
  return (
    <div
      className={`relative bg-white rounded-2xl shadow-lg border-2 overflow-hidden transform transition-all duration-300 hover:scale-105 ${
        plan.recommended
          ? 'border-brand-600 shadow-blue-200 lg:scale-105'
          : plan.badge === 'Best Seller'
          ? 'border-green-600 shadow-green-200'
          : 'border-gray-200 hover:border-blue-400'
      }`}
    >
      {plan.badge && (
        <div className={`absolute top-0 left-0 right-0 text-white text-center py-2 text-sm font-semibold ${
          plan.recommended
            ? 'bg-gradient-to-r from-brand-600 to-brand-800'
            : 'bg-gradient-to-r from-green-600 to-green-800'
        }`}>
          {plan.recommended ? (
            <>
              <Star className="w-4 h-4 inline mr-1" />
              {plan.badge}
            </>
          ) : (
            <>
              <Award className="w-4 h-4 inline mr-1" />
              {plan.badge}
            </>
          )}
        </div>
      )}

      <div className={`p-6 ${plan.badge ? 'pt-14' : ''}`}>
        <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
        <div className="mb-2">
          <span className="text-4xl font-bold text-brand-600">{plan.price}</span>
        </div>
        {plan.note && <p className="text-sm text-gray-600 mb-2">{plan.note}</p>}

        {plan.paymentNotice && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4">
            <p className="text-xs text-gray-500">{plan.paymentNotice}</p>
          </div>
        )}

        <div className="h-48 overflow-y-auto mb-6 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          <ul className="space-y-3 pr-2">
            {plan.features.map((feature, idx) => (
              <li key={idx} className="flex items-start">
                <Check className="w-5 h-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-700">{feature}</span>
              </li>
            ))}
          </ul>
        </div>

        <button
          onClick={onGetStarted}
          className={`w-full font-semibold py-3 px-6 rounded-lg transition-all duration-200 ${
            plan.recommended || plan.badge === 'Best Seller'
              ? 'bg-gradient-to-r from-brand-600 to-brand-800 text-white hover:from-brand-700 hover:to-brand-900 shadow-md'
              : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
          }`}
        >
          Get Started
        </button>

        <p className="text-xs text-gray-500 text-center mt-4">{plan.footnote}</p>
      </div>
    </div>
  );
};

const PricingSection = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { activePricingTab, setActivePricingTab, selectPackage, tmSignUpType } = usePricing();

  const config = TAB_CONFIG[activePricingTab];

  const handleTabKeyDown = (e, tab) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setActivePricingTab(tab);
    }
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setActivePricingTab(activePricingTab === TAB_1 ? TAB_2 : TAB_1);
    }
  };

  const handleGetStarted = (plan) => {
    selectPackage(plan.name, plan.price);
    console.log({
      tmSignUpType,
      packageTitle: plan.name,
      packageAmount: plan.price
    });
    setIsPopupOpen(true);
  };

  return (
    <>
      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Brand Protection Made Simple
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            We help businesses register intellectual property through reliable filing, monitoring, renewals, and complete trademark support. Whether your goal is to register a business name, claim ownership of a logo, or safeguard a slogan, our team makes the process clear and manageable. We assist brands across all 50 U.S. states and international markets, helping keep your intellectual property protected wherever your business operates.
          </p>
        </div>
      </section>

      {/* Pricing Tabs Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tab Navigation */}
          <div className="flex justify-center mb-12">
            <div
              role="tablist"
              aria-label="Pricing Options"
              className="inline-flex bg-white rounded-xl shadow-md border border-gray-200 p-1.5 flex-col sm:flex-row w-full sm:w-auto"
            >
              <button
                role="tab"
                aria-selected={activePricingTab === TAB_1}
                tabIndex={activePricingTab === TAB_1 ? 0 : -1}
                onKeyDown={(e) => handleTabKeyDown(e, TAB_1)}
                onClick={() => setActivePricingTab(TAB_1)}
                className={`px-6 py-3 rounded-lg font-semibold text-sm md:text-base transition-all duration-300 ${
                  activePricingTab === TAB_1
                    ? 'bg-gradient-to-r from-brand-600 to-brand-800 text-white shadow-md'
                    : 'text-gray-600 hover:text-brand-600 hover:bg-brand-50'
                }`}
              >
                Self Filing with Assistance
              </button>
              <button
                role="tab"
                aria-selected={activePricingTab === TAB_2}
                tabIndex={activePricingTab === TAB_2 ? 0 : -1}
                onKeyDown={(e) => handleTabKeyDown(e, TAB_2)}
                onClick={() => setActivePricingTab(TAB_2)}
                className={`px-6 py-3 rounded-lg font-semibold text-sm md:text-base transition-all duration-300 mt-1.5 sm:mt-0 sm:ml-1.5 ${
                  activePricingTab === TAB_2
                    ? 'bg-gradient-to-r from-brand-600 to-brand-800 text-white shadow-md'
                    : 'text-gray-600 hover:text-brand-600 hover:bg-brand-50'
                }`}
              >
                IP Attorney-Led Filing
              </button>
            </div>
          </div>

          {/* Tab Content with fade animation */}
          <div key={activePricingTab} className="animate-fadeIn">
            {/* Process Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16 max-w-6xl mx-auto">
              {config.processCards.map((card, index) => (
                <ProcessCard key={index} card={card} />
              ))}
            </div>

            {/* Pricing Section Header */}
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-gray-900 mb-3">
                {config.headerTitle}
              </h3>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {config.headerSubtitle}
              </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
              {config.pricingPlans.map((plan, index) => (
                <PricingCard
                  key={index}
                  plan={plan}
                  onGetStarted={() => handleGetStarted(plan)}
                />
              ))}
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              *USPTO filing fees are set by the United States Patent and Trademark Office and are paid directly during the application process.
            </p>
          </div>
        </div>
      </section>

      <UniversalPopupForm isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} />
    </>
  );
};

export default PricingSection;
